import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Plus, MoreHorizontal, Trash2, ArrowUp, ArrowDown, FolderPlus, PackagePlus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { useAuth } from '../../context/AuthContext';
import { fmtMoney } from '../../lib/format';

/**
 * Zona izquierda del editor: árbol de grupos y conceptos.
 */
export const GroupTree = ({ quotation, frozen, selectedConceptId, onSelectConcept, editor, onAddConcept, onAddFromCatalog }) => {
  const { canDelete } = useAuth();
  const [collapsed, setCollapsed] = useState({});
  const [newGroupName, setNewGroupName] = useState('');
  const [addingGroup, setAddingGroup] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const groups = quotation?.groups || [];

  const submitNewGroup = () => {
    if (newGroupName.trim()) {
      editor.addGroup.mutate(newGroupName.trim());
      setNewGroupName('');
      setAddingGroup(false);
    }
  };

  const submitRename = (groupId) => {
    if (renameValue.trim()) {
      editor.updateGroup.mutate({ groupId, patch: { nombre_grupo: renameValue.trim() } });
    }
    setRenamingId(null);
  };

  const moveGroup = (idx, dir) => {
    const ids = groups.map((g) => g.id);
    const target = idx + dir;
    if (target < 0 || target >= ids.length) return;
    [ids[idx], ids[target]] = [ids[target], ids[idx]];
    editor.reorderGroups.mutate(ids);
  };

  return (
    <div className="flex flex-col h-full" data-testid="apu-group-tree">
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
        <span className="th-label">Grupos / Partidas</span>
        {!frozen && (
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setAddingGroup(true)} data-testid="add-group-button" title="Nuevo grupo">
            <FolderPlus size={15} />
          </Button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="p-2 space-y-1 w-full">
          {addingGroup && (
            <div className="flex gap-1.5 p-1">
              <Input
                autoFocus
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submitNewGroup(); if (e.key === 'Escape') setAddingGroup(false); }}
                placeholder="Nombre del grupo"
                className="h-8 text-[13px]"
                data-testid="new-group-name-input"
              />
              <Button size="sm" className="h-8" onClick={submitNewGroup} data-testid="confirm-add-group-button">OK</Button>
            </div>
          )}
          {groups.length === 0 && !addingGroup && (
            <div className="text-center text-muted-foreground text-[13px] py-8 px-3">
              Sin grupos. Crea el primero con el botón +
            </div>
          )}
          {groups.map((g, idx) => (
            <div key={g.id} className="rounded-lg">
              <div className="group flex items-center gap-1 px-1.5 py-1.5 rounded-md hover:bg-card/70 min-w-0">
                <button
                  onClick={() => setCollapsed((c) => ({ ...c, [g.id]: !c[g.id] }))}
                  className="text-muted-foreground hover:text-foreground shrink-0"
                  data-testid={`toggle-group-${g.id}`}
                >
                  {collapsed[g.id] ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                </button>
                {renamingId === g.id ? (
                  <Input
                    autoFocus
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={() => submitRename(g.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter') submitRename(g.id); if (e.key === 'Escape') setRenamingId(null); }}
                    className="h-7 text-[13px] flex-1"
                    data-testid="rename-group-input"
                  />
                ) : (
                  <span className="flex-1 min-w-0 text-[13px] font-medium truncate" title={g.nombre_grupo}>{g.nombre_grupo}</span>
                )}
                <span className="text-[11px] text-muted-foreground font-mono shrink-0">{(g.concepts || []).length}</span>
                {!frozen && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0 opacity-60 group-hover:opacity-100" data-testid={`group-menu-${g.id}`}>
                        <MoreHorizontal size={13} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onAddConcept(g.id)} data-testid="group-action-add-concept">
                        <Plus size={13} className="mr-2" /> Nuevo concepto
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onAddFromCatalog(g.id)} data-testid="group-action-add-from-catalog">
                        <PackagePlus size={13} className="mr-2" /> Concepto del catálogo
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { setRenamingId(g.id); setRenameValue(g.nombre_grupo); }} data-testid="group-action-rename">
                        Renombrar
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => moveGroup(idx, -1)} disabled={idx === 0}>
                        <ArrowUp size={13} className="mr-2" /> Subir
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => moveGroup(idx, 1)} disabled={idx === groups.length - 1}>
                        <ArrowDown size={13} className="mr-2" /> Bajar
                      </DropdownMenuItem>
                      {canDelete && (
                        <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => editor.deleteGroup.mutate(g.id)} data-testid="group-action-delete">
                          <Trash2 size={13} className="mr-2" /> Eliminar grupo
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
              {!collapsed[g.id] && (
                <div className="ml-5 border-l border-border pl-1 space-y-0.5">
                  {(g.concepts || []).map((c) => (
                    <button
                      key={c.id}
                      onClick={() => onSelectConcept(c.id)}
                      data-testid={`tree-concept-${c.id}`}
                      className={`w-full text-left px-2 py-1.5 rounded-md text-[12.5px] flex items-center justify-between gap-2 transition-colors duration-150 ${
                        selectedConceptId === c.id ? 'bg-primary/10 text-foreground border-l-2 border-primary -ml-0.5' : 'text-muted-foreground hover:bg-card/70 hover:text-foreground'
                      }`}
                    >
                      <span className="truncate min-w-0">{c.numero_concepto} · {c.descripcion}</span>
                      <span className="money text-[11px] shrink-0">{fmtMoney(c.calculated_total, quotation.moneda)}</span>
                    </button>
                  ))}
                  {(g.concepts || []).length === 0 && (
                    <div className="text-[11.5px] text-muted-foreground/70 px-2 py-1.5">Sin conceptos</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
